import type { SelfCheckInOverview } from "@/features/pulse-surveys/schemas/self-check-in.schema";
import type { MoodOptionId } from "@/shared/pulse/mood/mood-display";

/// Discriminated state for the check-in CTA visual (T6). Pure derivation so
/// the visual layer (mood orb today, anything tomorrow) stays swappable and
/// the mapping stays unit-testable without React.
export type CheckInCtaState =
  | { kind: "read_only" }
  | { kind: "loading" }
  | { kind: "unavailable" }
  | { kind: "ready" }
  | { kind: "in_progress"; answered: number; total: number }
  /// `mood` and `streak` stay null until the shore overview exposes
  /// todayMood / streakDays; the visual renders a neutral done state then.
  | { kind: "done"; mood: MoodOptionId | null; streak: number | null };

export function deriveCheckInCtaState(args: {
  isReadOnly: boolean;
  isLoading: boolean;
  overview: SelfCheckInOverview | undefined;
}): CheckInCtaState {
  if (args.isReadOnly) return { kind: "read_only" };
  if (args.isLoading) return { kind: "loading" };
  const overview = args.overview;
  if (!overview) return { kind: "unavailable" };
  if (overview.availability === "done_today") {
    return {
      kind: "done",
      mood: overview.todayMood ?? null,
      streak: typeof overview.streakDays === "number" ? overview.streakDays : null,
    };
  }
  if (overview.availability === "in_progress") {
    const total = Array.isArray(overview.questionsSnapshot)
      ? (overview.questionsSnapshot as unknown[]).length
      : 2;
    return {
      kind: "in_progress",
      answered: overview.respondedQuestionIndexes.length,
      total,
    };
  }
  return { kind: "ready" };
}
